import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listDiffs, diffsCsvUrl } from '../../services/syncApi';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { DEFAULT_ENVIRONMENT, ENVIRONMENTS, ENVIRONMENT_LABELS } from '../../constants/markets';

const skuKey = (market, sku) => `${market}|${sku}`;
const parseSelection = (selectedSkus) => {
  const byMarket = { AU: [], NZ: [] };
  selectedSkus.forEach((key) => {
    const idx = key.indexOf('|');
    if (idx === -1) return;
    const market = key.slice(0, idx);
    const sku = key.slice(idx + 1);
    if (byMarket[market]) byMarket[market].push(sku);
  });
  return byMarket;
};

const BulkActionModal = ({ selectedSkus, onClose, onConfirm }) => {
  const byMarket = parseSelection(selectedSkus);
  const presetMarkets = Object.keys(byMarket).filter((m) => byMarket[m].length > 0);
  return (
    <div className="bulk-modal-backdrop" onClick={onClose}>
      <div className="bulk-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Sync {selectedSkus.size} selected SKU{selectedSkus.size === 1 ? '' : 's'}</h2>
        <p className="muted">
          You'll land on the Manual Sync page with the selection pre-filled. Because each row
          carries its own market, the Markets multi-select on that page will be locked to the
          markets present in your selection.
        </p>
        <ul>
          {presetMarkets.map((m) => (
            <li key={m}><strong>{m}:</strong> {byMarket[m].length} SKU{byMarket[m].length === 1 ? '' : 's'}</li>
          ))}
        </ul>
        <div className="footer">
          <button type="button" className="secondary" onClick={onClose}>Cancel</button>
          <button type="button" onClick={() => onConfirm(byMarket, presetMarkets)}>Go to Manual Sync</button>
        </div>
      </div>
    </div>
  );
};

const DiffExplorer = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    environment: DEFAULT_ENVIRONMENT,
    market_code: '',
    status: '',
    field_name: '',
    field_status: '',
    sku: '',
  });
  const [data, setData] = useState({ data: [], total: 0 });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  // Selection is keyed `${market}|${sku}` so a single SKU in two markets is two selectable rows.
  // Persisted to sessionStorage so refilter/pagination keeps the selection.
  const [selectedSkus, setSelectedSkus] = useState(() => {
    try {
      const raw = sessionStorage.getItem('diffSelectedSkus');
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const [showModal, setShowModal] = useState(false);
  const pageSize = 50;

  useEffect(() => {
    sessionStorage.setItem('diffSelectedSkus', JSON.stringify(Array.from(selectedSkus)));
  }, [selectedSkus]);

  // The two free-text filters are debounced; the dropdowns apply immediately.
  // Depending on the individual values rather than the filters object keeps a
  // keystroke in one of them from refetching before the debounce elapses.
  const debouncedSku = useDebouncedValue(filters.sku);
  const debouncedFieldName = useDebouncedValue(filters.field_name);
  const { environment, market_code: marketCode, status, field_status: fieldStatus } = filters;

  const query = useMemo(
    () => ({
      environment,
      market_code: marketCode,
      status,
      field_name: debouncedFieldName,
      field_status: fieldStatus,
      sku: debouncedSku,
    }),
    [environment, marketCode, status, fieldStatus, debouncedFieldName, debouncedSku],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...query, limit: pageSize, offset: page * pageSize };
      Object.keys(params).forEach((k) => (params[k] === '' || params[k] == null) && delete params[k]);
      const r = await listDiffs(params);
      setData(r);
    } finally {
      setLoading(false);
    }
  }, [query, page]);

  useEffect(() => { load(); }, [load]);

  const csvHref = diffsCsvUrl(
    Object.fromEntries(Object.entries(query).filter(([, v]) => v))
  );

  const toggleRow = (market, sku) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      const key = skuKey(market, sku);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const pageKeys = useMemo(() => data.data.map((r) => skuKey(r.market_code, r.sku)), [data.data]);
  const allPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selectedSkus.has(k));
  const togglePage = () => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageKeys.forEach((k) => next.delete(k));
      } else {
        pageKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  const clearAll = () => setSelectedSkus(new Set());

  const counts = useMemo(() => parseSelection(selectedSkus), [selectedSkus]);

  const confirmBulk = (byMarket, presetMarkets) => {
    if (presetMarkets.length === 0) return;
    // For now we pass the union to Manual Sync. The page's Markets selector
    // will be pre-locked to presetMarkets so the user can't accidentally
    // re-target a market that wasn't in their selection.
    const presetSkus = Array.from(new Set(presetMarkets.flatMap((m) => byMarket[m])));
    navigate('/jobs/run', {
      state: { presetSkus, presetMarket: presetMarkets[0], presetEnvironment: filters.environment },
    });
  };

  return (
    <div className="diff-explorer">
      <header>
        <h1>Diff Explorer</h1>
        <a className="button" href={csvHref}>Download CSV</a>
      </header>
      <section className="card">
        <div className="filters">
          <label>Environment
            <select value={filters.environment} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, environment: e.target.value })); }}>
              {ENVIRONMENTS.map((env) => (
                <option key={env} value={env}>{ENVIRONMENT_LABELS[env] || env}</option>
              ))}
            </select>
          </label>
          <label>Market
            <select value={filters.market_code} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, market_code: e.target.value })); }}>
              <option value="">All</option>
              <option value="AU">AU</option>
              <option value="NZ">NZ</option>
            </select>
          </label>
          <label>Status
            <select value={filters.status} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, status: e.target.value })); }}>
              <option value="">All</option>
              <option value="in_sync">in_sync</option>
              <option value="field_mismatch">field_mismatch</option>
              <option value="missing_product">missing_product</option>
              <option value="missing_field">missing_field</option>
              <option value="error">error</option>
            </select>
          </label>
          <label>Field
            <input value={filters.field_name} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, field_name: e.target.value })); }} placeholder="ProductLifecycle" />
          </label>
          <label>Field status
            <select value={filters.field_status} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, field_status: e.target.value })); }}>
              <option value="">Any</option>
              <option value="mismatch">mismatch</option>
              <option value="missing">missing</option>
              <option value="in_sync">in_sync</option>
            </select>
          </label>
          <label>SKU
            <input value={filters.sku} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, sku: e.target.value })); }} placeholder="050343, 0503*, 05?343" />
          </label>
        </div>
      </section>

      <section className="card">
        {loading && <p className="muted">Loading...</p>}
        <p className="muted">{data.total.toLocaleString()} total result(s)</p>
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={togglePage}
                  title="Select all on this page"
                />
              </th>
              <th>SKU</th>
              <th>Env</th>
              <th>Market</th>
              <th>Status</th>
              <th>Mismatches</th>
              <th>BC product ID</th>
              <th>Snapshot age</th>
              <th>Compared</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((row) => {
              const key = skuKey(row.market_code, row.sku);
              return (
                <tr key={row.id} className={selectedSkus.has(key) ? 'row-selected' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSkus.has(key)}
                      onChange={() => toggleRow(row.market_code, row.sku)}
                    />
                  </td>
                  <td>
                    <Link to={`/jobs/config/diffs/${row.market_code}/${encodeURIComponent(row.sku)}?environment=${filters.environment}`}>{row.sku}</Link>
                  </td>
                  <td>{row.environment || filters.environment}</td>
                  <td>{row.market_code}</td>
                  <td><span className={`status status-${row.status}`}>{row.status}</span></td>
                  <td>{row.mismatch_count}</td>
                  <td>{row.bc_product_id || '—'}</td>
                  <td>{row.snapshot_fetched_at ? new Date(row.snapshot_fetched_at).toLocaleString() : '—'}</td>
                  <td>{row.last_compared_at ? new Date(row.last_compared_at).toLocaleString() : '—'}</td>
                </tr>
              );
            })}
            {data.data.length === 0 && !loading && (
              <tr><td colSpan={9} className="muted">No diff rows match these filters.</td></tr>
            )}
          </tbody>
        </table>
        <div className="pagination">
          <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Prev</button>
          <span>Page {page + 1} of {Math.max(1, Math.ceil(data.total / pageSize))}</span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * pageSize >= data.total}>Next</button>
        </div>
      </section>

      {selectedSkus.size > 0 && (
        <div className="diff-bulk-bar">
          <div>
            <strong>{selectedSkus.size}</strong> selected
            <span className="market-counts">
              {counts.AU.length > 0 && <span className="market-au">  AU: {counts.AU.length}</span>}
              {counts.NZ.length > 0 && <span className="market-nz">  NZ: {counts.NZ.length}</span>}
            </span>
          </div>
          <div>
            <button type="button" className="secondary" onClick={clearAll}>Clear</button>
            <button type="button" onClick={() => setShowModal(true)} style={{ marginLeft: 8 }}>
              Send to Manual Sync →
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <BulkActionModal
          selectedSkus={selectedSkus}
          onClose={() => setShowModal(false)}
          onConfirm={(byMarket, presetMarkets) => {
            setShowModal(false);
            confirmBulk(byMarket, presetMarkets);
          }}
        />
      )}
    </div>
  );
};

export default DiffExplorer;
